import React, { useState } from 'react';
import { X, FileText, Calendar, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Edit3 } from 'lucide-react';

const AssessmentDetailsModal = ({ isOpen, onClose, assessment, caseName }) => {
  const [expandedCategory, setExpandedCategory] = useState('selfcare');

  if (!isOpen || !assessment) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Organize domains by category
  const categorizeResults = (results) => {
    const categories = {
      selfcare: {
        title: 'Self Care',
        domains: [],
        color: 'teal'
      },
      bladder: {
        title: 'Bladder Management',
        domains: [],
        color: 'blue'
      },
      bowel: {
        title: 'Bowel Management',
        domains: [],
        color: 'indigo'
      },
      mobility: {
        title: 'Mobility & Transfers',
        domains: [],
        color: 'purple'
      },
      locomotion: {
        title: 'Locomotion',
        domains: [],
        color: 'pink'
      },
      communication: {
        title: 'Communication',
        domains: [],
        color: 'green'
      },
      psychosocial: {
        title: 'Psychosocial',
        domains: [],
        color: 'yellow'
      },
      cognition: {
        title: 'Cognition',
        domains: [],
        color: 'orange'
      },
      adl: {
        title: 'Activities of Daily Living',
        domains: [],
        color: 'cyan'
      }
    };

    if (!results || typeof results !== 'object') return categories;

    for (const [key, value] of Object.entries(results)) {
      if (!value || typeof value !== 'object') continue;

      const domain = {
        key,
        name: formatDomainName(key),
        score: value.userScore !== null && value.userScore !== undefined ? value.userScore : value.score || 0,
        originalScore: value.score || 0,
        justification: value.justification || '',
        flagged: value.flaggedForReview || false,
        edited: value.userScore !== null && value.userScore !== undefined
      };

      // Categorize based on key prefix
      if (key.startsWith('selfcare_')) {
        categories.selfcare.domains.push(domain);
      } else if (key.startsWith('bladder_')) {
        categories.bladder.domains.push(domain);
      } else if (key.startsWith('bowel_')) {
        categories.bowel.domains.push(domain);
      } else if (key.startsWith('mobility_')) {
        categories.mobility.domains.push(domain);
      } else if (key.startsWith('locomotion_')) {
        categories.locomotion.domains.push(domain);
      } else if (key.startsWith('communication_')) {
        categories.communication.domains.push(domain);
      } else if (key.startsWith('psychosocial_')) {
        categories.psychosocial.domains.push(domain);
      } else if (key.startsWith('cognition_')) {
        categories.cognition.domains.push(domain);
      } else if (key.startsWith('adl_')) {
        categories.adl.domains.push(domain);
      }
    }

    return categories;
  };

  const formatDomainName = (key) => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getScoreColor = (score) => {
    if (score >= 6) return 'text-green-700 bg-green-50';
    if (score >= 4) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
  };

  const getScoreLabel = (score) => {
    const labels = {
      7: 'Complete Independence',
      6: 'Modified Independence',
      5: 'Supervision/Setup',
      4: 'Minimal Assistance',
      3: 'Moderate Assistance',
      2: 'Maximal Assistance',
      1: 'Total Assistance'
    };
    return labels[score] || 'Unknown';
  };

  const categories = categorizeResults(assessment.results);
  const summary = assessment.summary || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <FileText className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Assessment Details</h2>
              <p className="text-teal-100 text-sm">
                {caseName} • {formatDate(assessment.createdAt || assessment.date)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-4 bg-gray-50 border-b flex-shrink-0">
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-white rounded-lg p-3 border-2 border-teal-200">
              <div className="text-xs text-gray-600 mb-1">Total Score</div>
              <div className="text-2xl font-bold text-teal-700">
                {summary.totalScore || 0}
                <span className="text-sm text-gray-500 font-normal"> / {(summary.domainCount || 0) * 7}</span>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border-2 border-blue-200">
              <div className="text-xs text-gray-600 mb-1">Average Score</div>
              <div className="text-2xl font-bold text-blue-700">
                {summary.averageScore || 0}
                <span className="text-sm text-gray-500 font-normal"> / 7</span>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border-2 border-purple-200">
              <div className="text-xs text-gray-600 mb-1">Domains Assessed</div>
              <div className="text-2xl font-bold text-purple-700">{summary.domainCount || 0}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border-2 border-blue-300">
              <div className="text-xs text-gray-600 mb-1">Clinician Edits</div>
              <div className="text-2xl font-bold text-blue-600">{summary.editedCount || 0}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border-2 border-amber-200">
              <div className="text-xs text-gray-600 mb-1">Flagged for Review</div>
              <div className="text-2xl font-bold text-amber-700">{summary.flaggedForReview || 0}</div>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Edit Metadata Info */}
          {assessment.editMetadata && assessment.editMetadata.editCount > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Edit3 className="text-blue-600 mt-0.5" size={20} />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">Clinician Edits Applied</h4>
                  <p className="text-sm text-blue-800 mb-2">
                    {assessment.editMetadata.editCount} domain{assessment.editMetadata.editCount !== 1 ? 's' : ''} 
                    {' '}modified on {formatDate(assessment.editMetadata.editedAt)}
                  </p>
                  {assessment.editMetadata.editedDomains && assessment.editMetadata.editedDomains.length > 0 && (
                    <div className="text-xs text-blue-700">
                      <strong>Modified domains:</strong>{' '}
                      {assessment.editMetadata.editedDomains.map(d => formatDomainName(d)).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {Object.entries(categories).map(([categoryKey, category]) => {
            if (category.domains.length === 0) return null;

            const isExpanded = expandedCategory === categoryKey;
            const categoryTotal = category.domains.reduce((sum, d) => sum + d.score, 0);
            const categoryMax = category.domains.length * 7;

            return (
              <div key={categoryKey} className="mb-4">
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : categoryKey)}
                  className="w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-4 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-gray-900">{category.title}</h3>
                    <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">
                      {categoryTotal} / {categoryMax}
                    </span>
                    <span className="text-xs text-gray-500">
                      {category.domains.length} domain{category.domains.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {isExpanded && (
                  <div className="mt-2 space-y-2">
                    {category.domains.map((domain) => (
                      <div
                        key={domain.key}
                        className={`bg-white border-2 rounded-lg p-4 ${
                          domain.edited ? 'border-blue-300 bg-blue-50' : 
                          domain.flagged ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{domain.name}</h4>
                            {domain.edited && (
                              <span className="text-blue-600 flex items-center gap-1 text-xs">
                                <Edit3 size={14} />
                                Edited by Clinician
                              </span>
                            )}
                            {domain.flagged && (
                              <span className="text-amber-600 flex items-center gap-1 text-xs">
                                <AlertTriangle size={14} />
                                Flagged
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {domain.edited && (
                              <span className="text-xs text-gray-500 line-through">
                                AI: {domain.originalScore}
                              </span>
                            )}
                            <span className={`px-3 py-1 rounded-full font-bold text-sm ${getScoreColor(domain.score)}`}>
                              {domain.score}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {getScoreLabel(domain.score)}
                        </div>
                        <p className="text-sm text-gray-700">{domain.justification}</p>
                        {domain.edited && (
                          <div className="mt-2 pt-2 border-t border-blue-200">
                            <p className="text-xs text-blue-700">
                              <strong>Note:</strong> Score changed from {domain.originalScore} (AI) to {domain.score} (Clinician)
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 flex-shrink-0 border-t">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetailsModal;

